import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, ExternalLink, Save, Truck, Mail, Archive } from 'lucide-react';
import { COLORS } from '../constants';
import { fetchCarriers, updateStatus, saveShipment, saveContact, saveNotes, resendEmail, apiError } from './adminApi';
import { STATUS_META, TRANSITIONS, fmtMoney, fmtDate, hasAddress } from './orderMeta';

const EMPTY_ADDRESS = { name: '', line1: '', line2: '', city: '', postal_code: '', state: '', country: 'IT' };
const EMPTY_CUSTOMER = { email: '', name: '', phone: '' };

const Field = ({ label, value, onChange, type = 'text', testId, span }) => (
  <label className={`adm-field ${span ? 'span-2' : ''}`}>
    <span>{label}</span>
    <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
  </label>
);

const ItemsList = ({ order }) => (
  <ul className="adm-items" data-testid="order-items">
    {order.items.map((it, i) => (
      <li key={`${it.id}-${i}`}>
        <i style={{ background: COLORS[it.line_color] || '#fff' }} />
        <span className="adm-item-name">{it.name}</span>
        <span className="adm-muted">× {it.quantity}</span>
        <strong>{fmtMoney(Number(it.price) * Number(it.quantity), order.currency)}</strong>
      </li>
    ))}
    <li className="adm-items-total"><span>Totale pagato</span><strong data-testid="order-total">{fmtMoney(order.total, order.currency)}</strong></li>
  </ul>
);

const ContactForm = ({ order, onChanged }) => {
  const [customer, setCustomer] = useState({ ...EMPTY_CUSTOMER, ...(order.customer || {}) });
  const [address, setAddress] = useState({ ...EMPTY_ADDRESS, ...(order.shipping_address || {}) });
  const [busy, setBusy] = useState(false);
  const setC = (k) => (v) => setCustomer((p) => ({ ...p, [k]: v }));
  const setA = (k) => (v) => setAddress((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      onChanged(await saveContact(order.id, customer, address));
      toast.success('Dati cliente salvati');
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="adm-block" data-testid="order-contact">
      <div className="adm-block-head">
        <h4>Cliente &amp; indirizzo</h4>
        {!hasAddress(order) && <span className="adm-warn">Indirizzo mancante</span>}
      </div>
      <div className="adm-grid">
        <Field label="Nome" value={customer.name} onChange={setC('name')} testId="contact-name" />
        <Field label="Email" value={customer.email} onChange={setC('email')} type="email" testId="contact-email" />
        <Field label="Telefono" value={customer.phone} onChange={setC('phone')} testId="contact-phone" />
        <Field label="Destinatario" value={address.name} onChange={setA('name')} testId="address-name" />
        <Field label="Indirizzo" value={address.line1} onChange={setA('line1')} testId="address-line1" span />
        <Field label="Interno / c/o" value={address.line2} onChange={setA('line2')} testId="address-line2" span />
        <Field label="CAP" value={address.postal_code} onChange={setA('postal_code')} testId="address-postal" />
        <Field label="Città" value={address.city} onChange={setA('city')} testId="address-city" />
        <Field label="Provincia" value={address.state} onChange={setA('state')} testId="address-state" />
        <Field label="Paese" value={address.country} onChange={setA('country')} testId="address-country" />
      </div>
      <button className="adm-btn" onClick={save} disabled={busy} data-testid="contact-save"><Save size={14} /> Salva dati</button>
    </div>
  );
};

const ShipmentForm = ({ order, carriers, onChanged }) => {
  const [carrier, setCarrier] = useState(order.shipment?.carrier || 'poste');
  const [tracking, setTracking] = useState(order.shipment?.tracking_number || '');
  const [busy, setBusy] = useState(false);
  const locked = ['cancelled', 'refunded'].includes(order.status);

  const save = async () => {
    setBusy(true);
    try {
      onChanged(await saveShipment(order.id, { carrier, tracking_number: tracking }));
      toast.success(order.status === 'shipped' || order.status === 'delivered' ? 'Spedizione aggiornata' : 'Ordine segnato come spedito');
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="adm-block" data-testid="order-shipment">
      <div className="adm-block-head"><h4>Spedizione</h4>{order.shipment?.shipped_at && <span className="adm-muted adm-small">spedito il {fmtDate(order.shipment.shipped_at)}</span>}</div>
      <div className="adm-grid">
        <label className="adm-field">
          <span>Corriere</span>
          <select value={carrier} onChange={(e) => setCarrier(e.target.value)} disabled={locked} data-testid="shipment-carrier">
            {carriers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <Field label="Numero tracking" value={tracking} onChange={setTracking} testId="shipment-tracking" />
      </div>
      <div className="adm-actions">
        <button className="adm-btn primary" onClick={save} disabled={busy || locked} data-testid="shipment-save">
          <Truck size={14} /> {order.shipment ? 'Aggiorna spedizione' : 'Segna come spedito'}
        </button>
        {order.shipment?.tracking_url && (
          <a className="adm-btn ghost" href={order.shipment.tracking_url} target="_blank" rel="noreferrer" data-testid="shipment-track-link"><ExternalLink size={14} /> Traccia</a>
        )}
      </div>
    </div>
  );
};

const StatusActions = ({ order, onChanged }) => {
  const [busy, setBusy] = useState('');
  const go = async (status) => {
    if (['cancelled', 'refunded'].includes(status) && !window.confirm(`Confermi: ${STATUS_META[status].label.toLowerCase()} l'ordine ${order.order_number}?`)) return;
    setBusy(status);
    try {
      onChanged(await updateStatus(order.id, status));
      toast.success(`Stato aggiornato: ${STATUS_META[status].label}`);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(''); }
  };
  const next = TRANSITIONS[order.status] || [];
  if (!next.length) return null;
  return (
    <div className="adm-block" data-testid="order-status-actions">
      <div className="adm-block-head"><h4>Cambia stato</h4></div>
      <div className="adm-actions">
        {next.map((s) => (
          <button key={s} className={`adm-btn ${['cancelled', 'refunded'].includes(s) ? 'danger' : ''}`} style={{ '--c': STATUS_META[s].color }} onClick={() => go(s)} disabled={Boolean(busy)} data-testid={`status-action-${s}`}>
            {STATUS_META[s].label}
          </button>
        ))}
      </div>
    </div>
  );
};

const NotesForm = ({ order, onChanged }) => {
  const [notes, setNotes] = useState(order.notes || '');
  const save = async () => {
    try { onChanged(await saveNotes(order.id, notes)); toast.success('Note salvate'); } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <div className="adm-block">
      <div className="adm-block-head"><h4>Note interne</h4></div>
      <textarea className="adm-textarea" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Es. cliente chiede consegna al portiere" data-testid="order-notes" />
      <button className="adm-btn" onClick={save} data-testid="notes-save"><Save size={14} /> Salva note</button>
    </div>
  );
};

const EMAIL_KINDS = [
  { kind: 'confirmation', label: 'Conferma ordine' },
  { kind: 'shipping', label: 'Spedizione con tracking' },
];

const EmailsPanel = ({ order, onChanged }) => {
  const [busy, setBusy] = useState('');
  const emails = order.emails || {};
  const hasEmail = Boolean(order.customer?.email);
  const send = async (kind) => {
    setBusy(kind);
    try {
      onChanged(await resendEmail(order.id, kind));
      toast.success('Email inviata');
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(''); }
  };
  return (
    <div className="adm-block" data-testid="order-emails">
      <div className="adm-block-head"><h4>Email al cliente</h4>{!hasEmail && <span className="adm-warn">Email cliente mancante</span>}</div>
      <ul className="adm-emails">
        {EMAIL_KINDS.map(({ kind, label }) => {
          const sentAt = emails[`${kind}_sent_at`];
          const error = emails[`${kind}_error`];
          return (
            <li key={kind} data-testid={`email-${kind}`}>
              <Mail size={15} className={sentAt ? 'ok' : error ? 'err' : ''} />
              <div>
                <strong>{label}</strong>
                <span className="adm-muted adm-small">
                  {sentAt ? `inviata il ${fmtDate(sentAt)}` : error ? `errore: ${error}` : kind === 'shipping' && order.status !== 'shipped' && order.status !== 'delivered' ? 'parte automaticamente allo stato Spedito' : 'non inviata'}
                </span>
              </div>
              <button className="adm-btn" onClick={() => send(kind)} disabled={!hasEmail || Boolean(busy)} data-testid={`email-send-${kind}`}>
                {sentAt ? 'Reinvia' : 'Invia ora'}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const Timeline = ({ order }) => (
  <ol className="adm-timeline" data-testid="order-timeline">
    {[...(order.history || [])].reverse().map((h, i) => (
      <li key={i} style={{ '--c': STATUS_META[h.status]?.color || '#fff' }}>
        <strong>{STATUS_META[h.status]?.label || h.status}</strong>
        <span className="adm-muted adm-small">{fmtDate(h.at)}{h.note ? ` · ${h.note}` : ''}</span>
      </li>
    ))}
  </ol>
);

const OrderDetail = ({ order, onClose, onChanged }) => {
  const [carriers, setCarriers] = useState([]);
  useEffect(() => { fetchCarriers().then(setCarriers).catch(() => setCarriers([{ id: 'other', name: 'Altro corriere' }])); }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const meta = STATUS_META[order.status] || {};

  return (
    <div className="adm-drawer-backdrop" onClick={onClose} data-testid="order-detail">
      <aside className="adm-drawer" onClick={(e) => e.stopPropagation()}>
        <header className="adm-drawer-head">
          <div>
            <span className="eyebrow">Ordine</span>
            <h3 className="adm-mono" data-testid="order-detail-number">{order.order_number}</h3>
            <p className="adm-muted adm-small">{fmtDate(order.created_at)} · {order.source === 'free' ? 'ordine test (codice gratuito)' : 'Stripe'} · {order.session_id?.slice(0, 18)}…</p>
          </div>
          <div className="adm-head-badges">
            <span className="adm-badge lg" style={{ '--c': meta.color }} data-testid="order-detail-status">{meta.label}</span>
            {order.archived && <span className="adm-tag" data-testid="order-archived-tag"><Archive size={11} /> archiviato</span>}
          </div>
          <button className="adm-icon-btn" onClick={onClose} aria-label="Chiudi" data-testid="order-detail-close"><X size={16} /></button>
        </header>

        <div className="adm-drawer-body">
          <div className="adm-block">
            <div className="adm-block-head"><h4>Articoli</h4></div>
            <ItemsList order={order} />
          </div>
          <StatusActions order={order} onChanged={onChanged} />
          <ShipmentForm key={`${order.id}-${order.status}`} order={order} carriers={carriers} onChanged={onChanged} />
          <ContactForm key={order.id} order={order} onChanged={onChanged} />
          <EmailsPanel order={order} onChanged={onChanged} />
          <NotesForm key={`${order.id}-notes`} order={order} onChanged={onChanged} />
          <div className="adm-block">
            <div className="adm-block-head"><h4>Cronologia</h4></div>
            <Timeline order={order} />
          </div>
        </div>
      </aside>
    </div>
  );
};

export default OrderDetail;
